import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

import { REFERRAL_CONFIG, REFERRAL_COOKIE_NAME, REFERRAL_DISCOUNT_COOKIE_NAME } from '@/config/referral';
import { getAuthUser } from "@/lib/authUser";
export { REFERRAL_CONFIG, getStoredReferralCode } from '@/config/referral';

const REFERRAL_COOKIE_DAYS = 30;
const DISCOUNT_COOKIE_HOURS = REFERRAL_CONFIG.DISCOUNT_HOURS;

interface ReferralDiscount {
  referrerCode: string;
  discountPercent: number;
  expiresAt: Date;
  discountUsed: boolean;
}

interface ReferralCookieData {
  referrerCode: string;
  createdAt: number;
  expiresAt: number;
}

interface DiscountCookieData {
  referrerCode: string;
  discountPercent: number;
  createdAt: number;
  expiresAt: number;
}

// Cookie utilities
const setCookie = (name: string, value: string, expirationDate: Date) => {
  document.cookie = `${name}=${value}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;
};

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  return null;
};

const deleteCookie = (name: string) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

// Module-level guard to prevent duplicate initialization across multiple hook instances
let cookiesLoadedOnce = false;

export function useReferralTracking() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [referralDiscount, setReferralDiscount] = useState<ReferralDiscount | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [referrerCode, setReferrerCode] = useState<string | null>(null);

  // Check for referral from URL and set cookies
  const processReferralFromUrl = useCallback(async () => {
    const refCode = searchParams.get('ref');
    
    if (refCode) {
      console.log('[Referral] Processing referral code from URL:', refCode);
      
      // Set 30-day referral tracking cookie
      const referralExpiry = new Date();
      referralExpiry.setDate(referralExpiry.getDate() + REFERRAL_COOKIE_DAYS);
      
      const referralData: ReferralCookieData = {
        referrerCode: refCode,
        createdAt: Date.now(),
        expiresAt: referralExpiry.getTime(),
      };
      
      setCookie(REFERRAL_COOKIE_NAME, JSON.stringify(referralData), referralExpiry);
      setReferrerCode(refCode);
      
      // First, always set the discount cookie for anonymous users
      // The eligibility check happens at checkout time on the server
      const existingDiscountCookie = getCookie(REFERRAL_DISCOUNT_COOKIE_NAME);
      
      if (!existingDiscountCookie) {
        // Set 72-hour discount cookie with 40% discount
        const discountExpiry = new Date();
        discountExpiry.setHours(discountExpiry.getHours() + DISCOUNT_COOKIE_HOURS);
        
        const discountData: DiscountCookieData = {
          referrerCode: refCode,
          discountPercent: REFERRAL_CONFIG.DISCOUNT_PERCENT,
          createdAt: Date.now(),
          expiresAt: discountExpiry.getTime(),
        };
        
        setCookie(REFERRAL_DISCOUNT_COOKIE_NAME, JSON.stringify(discountData), discountExpiry);
        console.log('[Referral] Discount cookie set, expires:', discountExpiry);
        
        setReferralDiscount({
          referrerCode: refCode,
          discountPercent: REFERRAL_CONFIG.DISCOUNT_PERCENT,
          expiresAt: discountExpiry,
          discountUsed: false,
        });
      } else {
        console.log('[Referral] Discount cookie already exists');
        // Load existing discount cookie data
        try {
          const discountData: DiscountCookieData = JSON.parse(existingDiscountCookie);
          if (discountData.expiresAt > Date.now()) {
            setReferralDiscount({
              referrerCode: discountData.referrerCode,
              discountPercent: discountData.discountPercent,
              expiresAt: new Date(discountData.expiresAt),
              discountUsed: false,
            });
          }
        } catch (e) {
          console.error('[Referral] Error parsing existing discount cookie:', e);
        }
      }
      
      // Remove ref param from URL (clean URL)
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('ref');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Load existing cookies on mount
  const loadExistingCookies = useCallback(async () => {
    const isFirstLoad = !cookiesLoadedOnce;
    cookiesLoadedOnce = true;
    if (isFirstLoad) {
      console.log('[Referral] Loading existing cookies...');
    }

    // Check referral cookie
    const referralCookieValue = getCookie(REFERRAL_COOKIE_NAME);
    if (referralCookieValue) {
      try {
        const referralData: ReferralCookieData = JSON.parse(referralCookieValue);
        if (referralData.expiresAt > Date.now()) {
          setReferrerCode(referralData.referrerCode);
          if (isFirstLoad) console.log('[Referral] Found valid referral cookie:', referralData.referrerCode);
        } else {
          deleteCookie(REFERRAL_COOKIE_NAME);
          if (isFirstLoad) console.log('[Referral] Referral cookie expired, deleted');
        }
      } catch (e) {
        console.error('[Referral] Error parsing referral cookie:', e);
      }
    }
    
    // Check discount cookie - show banner for all users initially
    // The actual eligibility is verified server-side at checkout
    const discountCookieValue = getCookie(REFERRAL_DISCOUNT_COOKIE_NAME);
    if (discountCookieValue) {
      try {
        const discountData: DiscountCookieData = JSON.parse(discountCookieValue);
        if (discountData.expiresAt > Date.now()) {
          if (isFirstLoad) console.log('[Referral] Found valid discount cookie, showing banner');
          
          setReferralDiscount({
            referrerCode: discountData.referrerCode,
            discountPercent: discountData.discountPercent,
            expiresAt: new Date(discountData.expiresAt),
            discountUsed: false,
          });
        } else {
          deleteCookie(REFERRAL_DISCOUNT_COOKIE_NAME);
          if (isFirstLoad) console.log('[Referral] Discount cookie expired, deleted');
        }
      } catch (e) {
        console.error('[Referral] Error parsing discount cookie:', e);
      }
    } else {
      if (isFirstLoad) console.log('[Referral] No discount cookie found');
    }
    
    setIsLoading(false);
  }, []);

  useEffect(() => {
    let mounted = true;
    const init = async () => {
      await loadExistingCookies();
      if (mounted) await processReferralFromUrl();
    };
    init();
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mark discount as used (call after successful purchase)
  const markDiscountAsUsed = useCallback(async (purchaseType: 'subscription' | 'payg', amountSavedCents?: number) => {
    if (!referralDiscount) return;
    
    const { data: { user } } = await getAuthUser();
    
    if (user) {
      const { data: accountUser } = await supabase
        .from('account_users')
        .select('account_id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (accountUser?.account_id) {
        // Upsert into referral_discount_usage
        await supabase
          .from('referral_discount_usage')
          .upsert({
            account_id: accountUser.account_id,
            referrer_code: referralDiscount.referrerCode,
            discount_percent: referralDiscount.discountPercent,
            discount_used: true,
            discount_used_at: new Date().toISOString(),
            purchase_type: purchaseType,
            amount_saved_cents: amountSavedCents,
          }, { onConflict: 'account_id' });
      }
    }
    
    deleteCookie(REFERRAL_DISCOUNT_COOKIE_NAME);
    setReferralDiscount(null);
  }, [referralDiscount]);

  // Clear discount (when expired)
  const clearDiscount = useCallback(() => {
    deleteCookie(REFERRAL_DISCOUNT_COOKIE_NAME);
    setReferralDiscount(null);
  }, []);

  // Get the referral code for checkout
  const getReferralCodeForCheckout = useCallback(() => {
    return referrerCode;
  }, [referrerCode]);

  // Calculate discounted price (40% off)
  const calculateDiscountedPrice = useCallback((originalPrice: number): number => {
    if (!referralDiscount || referralDiscount.discountUsed) {
      return originalPrice;
    }
    return originalPrice * (1 - referralDiscount.discountPercent / 100);
  }, [referralDiscount]);

  // Get time remaining for discount
  const getTimeRemaining = useCallback(() => {
    if (!referralDiscount) return null;
    
    const now = new Date();
    const remaining = referralDiscount.expiresAt.getTime() - now.getTime();
    
    if (remaining <= 0) {
      clearDiscount();
      return null;
    }
    
    return {
      hours: Math.floor(remaining / (1000 * 60 * 60)),
      minutes: Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((remaining % (1000 * 60)) / 1000),
      total: remaining,
    };
  }, [referralDiscount, clearDiscount]);

  return {
    referralDiscount,
    referrerCode,
    isLoading,
    markDiscountAsUsed,
    clearDiscount,
    getReferralCodeForCheckout,
    calculateDiscountedPrice,
    getTimeRemaining,
    hasActiveDiscount: !!referralDiscount && !referralDiscount.discountUsed,
    discountPercent: REFERRAL_CONFIG.DISCOUNT_PERCENT,
  };
}
